/**
 * Web Presence v2 — Craft.js node components.
 *
 * Phase 1 of the layered builder (see web-presence-v2/README intent): proves
 * the new document model can host primitives AND a rich, pixel-locked component
 * (TrivenyHero) as first-class draggable/selectable nodes with editable props.
 *
 * Each node uses `useNode` for selection/drag and exposes a `craft` config with
 * default props + a related settings panel. Container is a Craft canvas (can
 * nest children) and supports basic layering (position + z-index) — the seed of
 * free-canvas/layered layout that the flat block model can't express.
 *
 * Wired into the Studio at /design/v2 (App.tsx). The same RESOLVER renders published
 * pages on the public apex, so every node here must render correctly read-only too.
 */

import React, { useContext, useEffect, useRef, useState } from 'react';
import { useNode, useEditor, Element } from '@craftjs/core';
import TrivenyHero from '../../admin/pages/TrivenyHero';
import { PublicSiteBlock, PUBLIC_DEFAULT_THEME } from '../../admin/pages/PublicBlockRenderer';
import { DeviceCtx } from './device';
import { BrandCtx } from './brand';
import { ICON_NAMES } from './icons';
import { ImageUpload } from './ImageUpload';
import { webPresenceApi } from '../../shared/services/platformApi';
import './anim.css';
import './responsive.css';

// ---------------------------------------------------------------------------
// Animation — entrance/scroll animation wrapper + a shared settings editor.
// `play` is false in the editor (resting state) and true on the public site
// (animate when scrolled into view). Rides in a node's `animation` prop.
// ---------------------------------------------------------------------------
export type AnimSpec = { type?: string; durationMs?: number; delayMs?: number };
const ANIM_TYPES = ['none', 'fade', 'slide-up', 'slide-down', 'slide-left', 'slide-right', 'zoom'];

export function Animated({ animation, play, children }: { animation?: AnimSpec; play: boolean; children: React.ReactNode }) {
  const type = animation?.type || 'none';
  const ref = useRef<HTMLDivElement>(null);
  const [inview, setInview] = useState(false);
  useEffect(() => {
    if (!play || type === 'none') { setInview(true); return; }
    const el = ref.current;
    if (!el || typeof IntersectionObserver === 'undefined') { setInview(true); return; }
    const obs = new IntersectionObserver(
      (entries) => entries.forEach((e) => { if (e.isIntersecting) { setInview(true); obs.disconnect(); } }),
      { threshold: 0.15 },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [play, type]);
  if (type === 'none') return <>{children}</>;
  return (
    <div
      ref={ref}
      className={`tv-anim tv-anim--${type}${inview ? ' tv-anim--in' : ''}`}
      style={{ transitionDuration: `${animation?.durationMs ?? 600}ms`, transitionDelay: `${animation?.delayMs ?? 0}ms` }}
    >
      {children}
    </div>
  );
}

// Shared animation controls for a node's settings panel.
function AnimationSettings({ animation, onChange }: { animation?: AnimSpec; onChange: (a: AnimSpec) => void }) {
  const a = animation || {};
  return (
    <div style={{ display: 'grid', gap: 8, borderTop: '1px dashed #e6e8ec', paddingTop: 10, marginTop: 4 }}>
      <div style={{ fontSize: 11, color: '#9aa3af', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Animation (on scroll)</div>
      <label style={{ fontSize: 12, color: '#6b7280' }}>Type
        <select style={cssToStyle(field)} value={a.type || 'none'} onChange={(e) => onChange({ ...a, type: e.target.value })}>
          {ANIM_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
      </label>
      {a.type && a.type !== 'none' && (
        <div style={{ display: 'flex', gap: 8 }}>
          <label style={{ fontSize: 12, color: '#6b7280', flex: 1 }}>Duration (ms)
            <input style={cssToStyle(field)} type="number" value={a.durationMs ?? 600} onChange={(e) => onChange({ ...a, durationMs: Number(e.target.value) })} />
          </label>
          <label style={{ fontSize: 12, color: '#6b7280', flex: 1 }}>Delay (ms)
            <input style={cssToStyle(field)} type="number" value={a.delayMs ?? 0} onChange={(e) => onChange({ ...a, delayMs: Number(e.target.value) })} />
          </label>
        </div>
      )}
    </div>
  );
}

// Resolve the Triveny login/signup origin from the current host (works in the
// editor on app.triveny.ai and on the public site at triveny.ai).
function trivenyAuthUrls() {
  const host = typeof window !== 'undefined' ? window.location.hostname : '';
  const apex = host.replace(/^(app|www|dev)\./, '');
  const origin = apex.endsWith('triveny.ai') ? `https://app.${apex}` : '';
  return { loginUrl: origin ? `${origin}/login` : '#', signupUrl: origin ? `${origin}/signup` : undefined };
}

const field = 'display:block;width:100%;margin-top:4px;padding:6px 8px;border:1px solid #d7dbe0;border-radius:6px;font-size:13px';
// Quick-pick color swatches (brand + common). The native picker still offers the full spectrum.
const SWATCHES = ['#14171c', '#ffffff', '#7468ae', '#009a83', '#46b1e5', '#f43f5e', '#f59e0b', '#10b981', '#3b82f6', '#8b5cf6', '#ec4899', '#fafaf7', '#f1f5f9', '#64748b'];
// Quick-pick emoji icons for blocks that use icon glyphs (e.g. features).
const EMOJI_ICONS = ['✨', '🚀', '🔒', '📈', '💡', '⚡', '🎯', '🤝', '📣', '📊', '🛠️', '❤️', '🌐', '📅', '💬', '✅', '⭐', '🔔', '📦', '🧩'];

// ---------------------------------------------------------------------------
// Container — a Craft canvas node. Holds children; supports flow + layering.
// ---------------------------------------------------------------------------
// --- Responsive per-property overrides ------------------------------------
// Small high-value set overridable per breakpoint. Editor previews via inline
// style (driven by the device switcher); the public render emits real CSS media
// queries (scoped by node id) so it responds to the actual viewport.
type ResStyle = { padding?: number; background?: string; textAlign?: string; fontSize?: number };
type Responsive = { tablet?: ResStyle; mobile?: ResStyle };
const RES_PX = new Set(['padding', 'fontSize']);
function kebab(k: string) { return k.replace(/[A-Z]/g, (m) => '-' + m.toLowerCase()); }
function resDecl(o: ResStyle = {}): string {
  return Object.entries(o)
    .filter(([, v]) => v !== undefined && v !== '' && v !== null)
    .map(([k, v]) => `${kebab(k)}:${v}${typeof v === 'number' && RES_PX.has(k) ? 'px' : ''}`)
    .join(';');
}
function resMediaCss(cls: string, base: ResStyle, r?: Responsive): string {
  let css = `.${cls}{${resDecl(base)}}`;
  const t = resDecl(r?.tablet); if (t) css += `@media (min-width:641px) and (max-width:1024px){.${cls}{${t}}}`;
  const m = resDecl(r?.mobile); if (m) css += `@media (max-width:640px){.${cls}{${m}}}`;
  return css;
}
function resStyleObj(o: ResStyle = {}): React.CSSProperties {
  const out: any = {};
  Object.entries(o).forEach(([k, v]) => { if (v !== undefined && v !== '') out[k] = typeof v === 'number' && RES_PX.has(k) ? `${v}px` : v; });
  return out;
}

// Reusable per-breakpoint override editor (used by Container + every BlockNode).
function ResponsiveOverridesEditor({ responsive, onPatch }: { responsive?: Responsive; onPatch: (bp: 'tablet' | 'mobile', patch: ResStyle) => void }) {
  const [bp, setBp] = useState<'tablet' | 'mobile'>('tablet');
  const ov: ResStyle = (responsive && (responsive as any)[bp]) || {};
  return (
    <div style={{ borderTop: '1px dashed #e6e8ec', paddingTop: 10, marginTop: 4, display: 'grid', gap: 8 }}>
      <div style={{ fontSize: 11, color: '#9aa3af', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Responsive overrides</div>
      <div style={{ display: 'flex', gap: 6 }}>
        {(['tablet', 'mobile'] as const).map((d) => (
          <button key={d} onClick={() => setBp(d)} style={{ flex: 1, padding: '5px 8px', borderRadius: 6, fontSize: 12, fontWeight: 600, textTransform: 'capitalize', cursor: 'pointer', border: bp === d ? '1px solid #7468ae' : '1px solid #e6e8ec', background: bp === d ? '#f7f4ff' : '#fff', color: bp === d ? '#5b4b9e' : '#6b7280' }}>{d}</button>
        ))}
      </div>
      <label style={{ fontSize: 12, color: '#6b7280' }}>Padding (px)
        <input style={cssToStyle(field)} type="number" value={ov.padding ?? ''} placeholder="inherit" onChange={(e) => onPatch(bp, { padding: e.target.value === '' ? undefined : Number(e.target.value) })} />
      </label>
      <label style={{ fontSize: 12, color: '#6b7280' }}>Background
        <input style={cssToStyle(field)} type="text" value={ov.background ?? ''} placeholder="inherit" onChange={(e) => onPatch(bp, { background: e.target.value || undefined })} />
      </label>
      <label style={{ fontSize: 12, color: '#6b7280' }}>Text align
        <select style={cssToStyle(field)} value={ov.textAlign ?? ''} onChange={(e) => onPatch(bp, { textAlign: e.target.value || undefined })}>
          <option value="">inherit</option><option value="left">left</option><option value="center">center</option><option value="right">right</option>
        </select>
      </label>
      <label style={{ fontSize: 12, color: '#6b7280' }}>Font size (px)
        <input style={cssToStyle(field)} type="number" value={ov.fontSize ?? ''} placeholder="inherit" onChange={(e) => onPatch(bp, { fontSize: e.target.value === '' ? undefined : Number(e.target.value) })} />
      </label>
    </div>
  );
}

// Patch a node's responsive[bp] via setProp (shared by Container + BlockNode settings).
function patchResponsive(setProp: (cb: (p: any) => void) => void, bp: 'tablet' | 'mobile', patch: ResStyle) {
  setProp((p: any) => { p.responsive = { ...(p.responsive || {}), [bp]: { ...((p.responsive || {})[bp] || {}), ...patch } }; });
}

export const Container = ({ background, padding, minHeight, display, position, zIndex, responsive, children }: any) => {
  const { id, connectors: { connect, drag } } = useNode();
  const { enabled } = useEditor((state) => ({ enabled: state.options.enabled }));
  const device = useContext(DeviceCtx);
  const cls = `tvc-${id}`;
  const staticStyle: React.CSSProperties = {
    minHeight,
    display,
    position: position || 'relative',
    zIndex,
    flexDirection: display === 'flex' ? 'column' : undefined,
    gap: display === 'flex' ? 12 : undefined,
    borderRadius: 8,
  };
  const baseRes: ResStyle = { padding, background };

  if (enabled) {
    const ov = device !== 'desktop' ? (responsive?.[device] || {}) : {};
    return (
      <div ref={(ref) => ref && connect(drag(ref))} style={{ ...staticStyle, ...resStyleObj({ ...baseRes, ...ov }) }}>
        {children}
      </div>
    );
  }
  return (
    <>
      <style dangerouslySetInnerHTML={{ __html: resMediaCss(cls, baseRes, responsive) }} />
      <div ref={(ref) => ref && connect(drag(ref))} className={cls} style={staticStyle}>
        {children}
      </div>
    </>
  );
};
const ContainerSettings = () => {
  const { props, actions: { setProp } } = useNode((node) => ({ props: node.data.props }));
  const brand = useContext(BrandCtx);
  const spacing = (brand?.tokens || []).filter((t) => (t.group || '') === 'spacing' && t.value);
  return (
    <div style={{ display: 'grid', gap: 10 }}>
      <label style={{ fontSize: 12, color: '#6b7280' }}>Background
        <input style={cssToStyle(field)} type="text" value={props.background || ''} onChange={(e) => setProp((p: any) => (p.background = e.target.value))} />
      </label>
      <div>
        <label style={{ fontSize: 12, color: '#6b7280' }}>Padding (px)
          <input style={cssToStyle(field)} type="number" value={parseInt(props.padding) || 0} onChange={(e) => setProp((p: any) => (p.padding = Number(e.target.value)))} />
        </label>
        {spacing.length > 0 && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 6 }}>
            {spacing.map((t) => (
              <button key={t.name} title={t.value} onClick={() => setProp((p: any) => (p.padding = parseInt(t.value, 10) || 0))}
                style={{ fontSize: 11, padding: '3px 7px', borderRadius: 999, cursor: 'pointer', border: '1px solid #d9c9f3', background: '#f7f4ff', color: '#5b4b9e' }}>{t.name}</button>
            ))}
          </div>
        )}
      </div>
      <label style={{ fontSize: 12, color: '#6b7280' }}>Min height (px)
        <input style={cssToStyle(field)} type="number" value={parseInt(props.minHeight) || 0} onChange={(e) => setProp((p: any) => (p.minHeight = Number(e.target.value)))} />
      </label>
      <label style={{ fontSize: 12, color: '#6b7280' }}>Layout
        <select style={cssToStyle(field)} value={props.display || 'block'} onChange={(e) => setProp((p: any) => (p.display = e.target.value))}>
          <option value="block">Stack (block)</option>
          <option value="flex">Flex column</option>
        </select>
      </label>
      <label style={{ fontSize: 12, color: '#6b7280' }}>Position
        <select style={cssToStyle(field)} value={props.position || 'relative'} onChange={(e) => setProp((p: any) => (p.position = e.target.value))}>
          <option value="relative">In flow (relative)</option>
          <option value="absolute">Layered (absolute)</option>
        </select>
      </label>
      <label style={{ fontSize: 12, color: '#6b7280' }}>z-index (layer)
        <input style={cssToStyle(field)} type="number" value={props.zIndex ?? 0} onChange={(e) => setProp((p: any) => (p.zIndex = Number(e.target.value)))} />
      </label>

      <ResponsiveOverridesEditor responsive={props.responsive} onPatch={(bp, patch) => patchResponsive(setProp, bp, patch)} />
    </div>
  );
};
Container.craft = {
  displayName: 'Container',
  props: { background: '#ffffff', padding: 24, minHeight: 80, display: 'block', position: 'relative', zIndex: 0, responsive: {} },
  rules: { canMoveIn: () => true },
  related: { settings: ContainerSettings },
};

// ---------------------------------------------------------------------------
// Text
// ---------------------------------------------------------------------------
export const TextNode = ({ text, fontSize, color, weight, align, lineHeight, letterSpacing, fontFamily }: any) => {
  const { connectors: { connect, drag }, actions: { setProp }, selected } = useNode((n) => ({ selected: n.events.selected }));
  const [editable, setEditable] = React.useState(false);
  React.useEffect(() => { if (!selected) setEditable(false); }, [selected]);
  return (
    <div
      ref={(ref) => ref && connect(drag(ref))}
      onDoubleClick={() => setEditable(true)}
      style={{ fontSize, color, fontWeight: weight, textAlign: align, lineHeight: lineHeight || undefined, letterSpacing: letterSpacing ? `${letterSpacing}px` : undefined, fontFamily: fontFamily || undefined }}
    >
      <span
        contentEditable={editable}
        suppressContentEditableWarning
        onBlur={(e) => { setProp((p: any) => (p.text = e.currentTarget.textContent || '')); setEditable(false); }}
      >
        {text}
      </span>
    </div>
  );
};
// A consistent type ramp (label → size/weight/line-height) — click to apply the whole
// step, so text stays on a scale instead of arbitrary sizes.
const TYPE_SCALE: Array<[string, number, number, number]> = [
  ['Caption', 13, 500, 1.5],
  ['Body', 16, 500, 1.6],
  ['Lead', 20, 500, 1.55],
  ['H3', 24, 700, 1.3],
  ['H2', 32, 700, 1.2],
  ['H1', 44, 800, 1.1],
  ['Display', 60, 800, 1.05],
];
const FONT_STACKS: Array<[string, string]> = [
  ['Inherit', ''],
  ['Sans (Inter)', "'Inter', -apple-system, system-ui, sans-serif"],
  ['Serif', "Georgia, 'Times New Roman', serif"],
  ['Mono', "'JetBrains Mono', ui-monospace, Menlo, monospace"],
];
const TextSettings = () => {
  const { props, actions: { setProp } } = useNode((node) => ({ props: node.data.props }));
  const brand = useContext(BrandCtx);
  const fontTokens = (brand?.tokens || []).filter((t) => (t.group || '') === 'typography' && t.value);
  return (
    <div style={{ display: 'grid', gap: 10 }}>
      <label style={{ fontSize: 12, color: '#6b7280' }}>Text
        <textarea style={cssToStyle(field)} rows={2} value={props.text || ''} onChange={(e) => setProp((p: any) => (p.text = e.target.value))} />
      </label>
      <div>
        <div style={{ fontSize: 11, color: '#9aa3af', marginBottom: 4 }}>Type scale</div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
          {TYPE_SCALE.map(([label, size, wt, lh]) => (
            <button key={label} title={`${size}px`} onClick={() => setProp((p: any) => { p.fontSize = size; p.weight = wt; p.lineHeight = lh; })}
              style={{ fontSize: 11.5, padding: '4px 8px', borderRadius: 6, cursor: 'pointer', border: props.fontSize === size ? '1px solid #7468ae' : '1px solid #e6e8ec', background: props.fontSize === size ? '#f7f4ff' : '#fff', color: props.fontSize === size ? '#5b4b9e' : '#6b7280' }}>{label}</button>
          ))}
        </div>
      </div>
      <div style={{ display: 'flex', gap: 8 }}>
        <label style={{ fontSize: 12, color: '#6b7280', flex: 1 }}>Size (px)
          <input style={cssToStyle(field)} type="number" value={props.fontSize || 16} onChange={(e) => setProp((p: any) => (p.fontSize = Number(e.target.value)))} />
        </label>
        <label style={{ fontSize: 12, color: '#6b7280', flex: 1 }}>Weight
          <select style={cssToStyle(field)} value={props.weight || 500} onChange={(e) => setProp((p: any) => (p.weight = Number(e.target.value)))}>
            {[400, 500, 600, 700, 800].map((w) => <option key={w} value={w}>{w}</option>)}
          </select>
        </label>
      </div>
      <div style={{ display: 'flex', gap: 8 }}>
        <label style={{ fontSize: 12, color: '#6b7280', flex: 1 }}>Line height
          <input style={cssToStyle(field)} type="number" step="0.05" value={props.lineHeight ?? 1.4} onChange={(e) => setProp((p: any) => (p.lineHeight = Number(e.target.value)))} />
        </label>
        <label style={{ fontSize: 12, color: '#6b7280', flex: 1 }}>Letter spacing (px)
          <input style={cssToStyle(field)} type="number" step="0.5" value={props.letterSpacing ?? 0} onChange={(e) => setProp((p: any) => (p.letterSpacing = Number(e.target.value)))} />
        </label>
      </div>
      <label style={{ fontSize: 12, color: '#6b7280' }}>Font
        <select style={cssToStyle(field)} value={props.fontFamily || ''} onChange={(e) => setProp((p: any) => (p.fontFamily = e.target.value))}>
          {FONT_STACKS.map(([label, stack]) => <option key={label} value={stack}>{label}</option>)}
          {fontTokens.map((t) => <option key={t.name} value={t.value}>{t.name} (brand)</option>)}
        </select>
      </label>
      <div style={{ display: 'flex', gap: 8 }}>
        <label style={{ fontSize: 12, color: '#6b7280', flex: 1 }}>Color
          <input style={cssToStyle(field)} type="text" value={props.color || ''} onChange={(e) => setProp((p: any) => (p.color = e.target.value))} />
        </label>
        <label style={{ fontSize: 12, color: '#6b7280', flex: 1 }}>Align
          <select style={cssToStyle(field)} value={props.align || 'left'} onChange={(e) => setProp((p: any) => (p.align = e.target.value))}>
            <option>left</option><option>center</option><option>right</option>
          </select>
        </label>
      </div>
    </div>
  );
};
TextNode.craft = {
  displayName: 'Text',
  props: { text: 'Edit me', fontSize: 18, color: '#14171c', weight: 500, align: 'left', lineHeight: 1.4, letterSpacing: 0, fontFamily: '' },
  related: { settings: TextSettings },
};

// ---------------------------------------------------------------------------
// Image
// ---------------------------------------------------------------------------
export const ImageNode = ({ src, alt, radius, width }: any) => {
  const { connectors: { connect, drag } } = useNode();
  return (
    <div ref={(ref) => ref && connect(drag(ref))}>
      {src ? (
        <img src={src} alt={alt || ''} style={{ width: width || '100%', borderRadius: radius, display: 'block' }} />
      ) : (
        <div style={{ padding: 24, textAlign: 'center', color: '#9aa3af', border: '1px dashed #d7dbe0', borderRadius: 8 }}>Image — set a URL</div>
      )}
    </div>
  );
};
const ImageSettings = () => {
  const { props, actions: { setProp } } = useNode((node) => ({ props: node.data.props }));
  return (
    <div style={{ display: 'grid', gap: 10 }}>
      <ImageUpload label="Image" value={props.src || ''} onChange={(v) => setProp((p: any) => (p.src = v || ''))} />
      <StockPicker kind="image" label="Browse stock photos" onPick={(it) => setProp((p: any) => (p.src = it.url))} />
      <label style={{ fontSize: 12, color: '#6b7280' }}>Width (CSS)
        <input style={cssToStyle(field)} type="text" value={props.width || ''} onChange={(e) => setProp((p: any) => (p.width = e.target.value))} placeholder="100% or 320px" />
      </label>
      <label style={{ fontSize: 12, color: '#6b7280' }}>Corner radius (px)
        <input style={cssToStyle(field)} type="number" value={props.radius || 0} onChange={(e) => setProp((p: any) => (p.radius = Number(e.target.value)))} />
      </label>
    </div>
  );
};
ImageNode.craft = {
  displayName: 'Image',
  props: { src: '', alt: '', radius: 10, width: '100%' },
  related: { settings: ImageSettings },
};

// ---------------------------------------------------------------------------
// TrivenyHero — the rich, pixel-locked, internally-layered component as a node.
// This is the Phase-1 proof: a complex animated/layered hero lives in the new
// builder as a first-class editable node (not a hardcoded escape hatch).
// ---------------------------------------------------------------------------
export const TrivenyHeroNode = ({ design, market, engage, loginLabel, getStartedLabel, brandName, footerNote, logoSrc, launchers }: any) => {
  const { connectors: { connect, drag } } = useNode();
  const { enabled } = useEditor((state) => ({ enabled: state.options.enabled }));
  const { loginUrl, signupUrl } = trivenyAuthUrls();
  return (
    <div ref={(ref) => ref && connect(drag(ref))} style={{ position: 'relative', height: enabled ? 600 : '100vh', overflow: 'hidden', borderRadius: enabled ? 8 : 0 }}>
      {/* In the editor: static + non-interactive so canvas clicks select the
          node. In read-only/public render: live (cycling + clickable links). */}
      <div style={{ pointerEvents: enabled ? 'none' : 'auto', height: '100%' }}>
        <TrivenyHero
          contained
          cycleMs={enabled ? 0 : 3600}
          loginUrl={loginUrl}
          loginLabel={loginLabel || 'Log in'}
          signupUrl={enabled ? undefined : signupUrl}
          getStartedLabel={getStartedLabel}
          brandName={brandName}
          footerNote={footerNote}
          logoSrc={logoSrc || undefined}
          launchers={launchers}
          statements={{ Design: design, Market: market, Engage: engage }}
        />
      </div>
    </div>
  );
};
const TrivenyHeroSettings = () => {
  const { props, actions: { setProp } } = useNode((node) => ({ props: node.data.props }));
  const fieldRow = (label: string, key: string, ph = '') => (
    <label style={{ fontSize: 12, color: '#6b7280' }}>{label}
      <input style={cssToStyle(field)} type="text" value={props[key] ?? ''} placeholder={ph} onChange={(e) => setProp((p: any) => (p[key] = e.target.value))} />
    </label>
  );
  // Per-pillar launcher TEXT editor (icons stay per slot).
  const PILLARS: Array<['Design' | 'Market' | 'Engage', string]> = [['Design', 'Create a landing page'], ['Market', 'Launch a campaign'], ['Engage', 'Manage pipeline']];
  const lov = (p: string) => (props.launchers && props.launchers[p]) || {};
  const setLov = (p: string, patch: any) => setProp((d: any) => { d.launchers = { ...(d.launchers || {}), [p]: { ...((d.launchers || {})[p] || {}), ...patch } }; });
  return (
    <div style={{ display: 'grid', gap: 10 }}>
      <p style={{ fontSize: 11, color: '#9aa3af', margin: 0 }}>The pixel-locked Triveny hero — layout + icons are fixed; all TEXT (and the logo) is editable here.</p>
      <div style={{ fontSize: 11, color: '#9aa3af', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Statements (cycle)</div>
      {(['design', 'market', 'engage'] as const).map((k) => (
        <label key={k} style={{ fontSize: 12, color: '#6b7280', textTransform: 'capitalize' }}>{k}
          <input style={cssToStyle(field)} type="text" value={props[k] || ''} onChange={(e) => setProp((p: any) => (p[k] = e.target.value))} />
        </label>
      ))}
      <div style={{ fontSize: 11, color: '#9aa3af', textTransform: 'uppercase', letterSpacing: '0.06em', borderTop: '1px dashed #e6e8ec', paddingTop: 10 }}>Brand & nav</div>
      <ImageUpload label="Logo" value={props.logoSrc || ''} onChange={(v) => setProp((p: any) => (p.logoSrc = v || ''))} />
      {fieldRow('Log in label', 'loginLabel', 'Log in')}
      {fieldRow('Get started label', 'getStartedLabel', 'Get started')}
      {fieldRow('Brand name (footer)', 'brandName', 'Triveny')}
      {fieldRow('Footer note', 'footerNote', 'All rights reserved')}
      <div style={{ fontSize: 11, color: '#9aa3af', textTransform: 'uppercase', letterSpacing: '0.06em', borderTop: '1px dashed #e6e8ec', paddingTop: 10 }}>Launchers (text)</div>
      {PILLARS.map(([p, ph]) => (
        <div key={p} style={{ border: '1px solid #eef0f3', borderRadius: 8, padding: 8, display: 'grid', gap: 6 }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: '#14171c' }}>{p}</div>
          <input style={cssToStyle(field)} type="text" placeholder={`Primary title (${ph})`} value={lov(p).primaryTitle ?? ''} onChange={(e) => setLov(p, { primaryTitle: e.target.value || undefined })} />
          <input style={cssToStyle(field)} type="text" placeholder="Primary subtitle" value={lov(p).primarySub ?? ''} onChange={(e) => setLov(p, { primarySub: e.target.value || undefined })} />
          <select style={cssToStyle(field)} value={lov(p).primaryIcon ?? ''} onChange={(e) => setLov(p, { primaryIcon: e.target.value || undefined })}>
            <option value="">Primary icon (default)</option>
            {ICON_NAMES.map((n) => <option key={n} value={n}>{n}</option>)}
          </select>
          <textarea style={cssToStyle(field)} rows={3} placeholder="Secondary chips (one per line — leave blank to keep defaults)" value={Array.isArray(lov(p).secondary) ? lov(p).secondary.join('\n') : ''} onChange={(e) => { const arr = e.target.value.split('\n').map((s) => s.trim()).filter(Boolean); setLov(p, { secondary: arr.length ? arr : undefined }); }} />
        </div>
      ))}
    </div>
  );
};
TrivenyHeroNode.craft = {
  displayName: 'Triveny Hero',
  props: { design: 'Prompt it. Create it.', market: 'All your GTM tools in one place.', engage: 'Move every deal forward.', loginLabel: 'Log in', getStartedLabel: 'Get started', brandName: 'Triveny', footerNote: 'All rights reserved', logoSrc: '', launchers: {} },
  related: { settings: TrivenyHeroSettings },
};

// ---------------------------------------------------------------------------
// BlockNode — the unified node for ALL flat block types (hero, features, cta,
// stats, testimonials, faq, pricing, gallery, heading, button, video, …).
// Renders through the SAME PublicSiteBlock the public site uses, so the canvas
// matches what ships (parity by construction). One node, many block types =
// the unified design model (no separate "v1 stackable" product).
// ---------------------------------------------------------------------------
export const BLOCK_DEFAULTS: Record<string, any> = {
  hero: { headline: 'Your headline here', subheadline: 'A short supporting line.', ctaButtons: [{ id: 'b1', text: 'Get started', url: '#', style: 'primary' }] },
  heading: { text: 'Section heading', level: '2' },
  features: { title: 'Features', features: [
    { icon: '✨', title: 'Fast', description: 'Describe this feature.' },
    { icon: '🔒', title: 'Secure', description: 'Describe this feature.' },
    { icon: '📈', title: 'Scalable', description: 'Describe this feature.' },
  ] },
  cta: { title: 'Ready to start?', description: 'A persuasive closing line.', buttons: [{ id: 'c1', text: 'Get started', url: '#', style: 'primary' }] },
  stats: { stats: [{ value: '99%', label: 'Uptime' }, { value: '24/7', label: 'Support' }, { value: '10k+', label: 'Users' }] },
  testimonials: { testimonials: [{ quote: 'This is great.', author: 'Jane Doe', role: 'CEO', company: 'Acme' }] },
  faq: { title: 'FAQ', faqs: [{ question: 'A question?', answer: 'The answer.' }] },
  pricing: { plans: [{ name: 'Starter', price: '$0', period: 'mo', features: ['Feature one', 'Feature two'], ctaText: 'Choose', ctaUrl: '#' }] },
  gallery: { images: [] },
  button: { text: 'Click me', url: '#' },
  video: { videoUrl: '', videoType: 'youtube' },
  form: { title: 'Get in touch', description: '', submitLabel: 'Submit', formId: '', formName: '', fields: [], successMessage: '' },
};

export const BlockNode = ({ blockType, content, styles, animation, hideOn, responsive }: any) => {
  const { id, connectors: { connect, drag } } = useNode();
  const { enabled } = useEditor((state) => ({ enabled: state.options.enabled }));
  const device = useContext(DeviceCtx);
  const brand = useContext(BrandCtx);
  // Brand cascade: blocks pick up brand primary + text colors (the public theme
  // contract is {primary, ink, muted, font}). Brand accent/background have no
  // theme slot, so they're exposed instead as named swatches in each block's
  // color pickers (see BlockSettings → brandSwatches). Per-block Style overrides
  // still win, since they're applied on top inside PublicSiteBlock.
  const theme = (brand.primary || brand.text)
    ? { ...PUBLIC_DEFAULT_THEME, primary: brand.primary || PUBLIC_DEFAULT_THEME.primary, ink: brand.text || PUBLIC_DEFAULT_THEME.ink }
    : PUBLIC_DEFAULT_THEME;
  const respCls = `tvb-${id}`;
  // Defensive: a 'triveny_hero' should be a TrivenyHeroNode, but older docs may
  // hold it as a BlockNode. PublicSiteBlock can't render it → would be blank, so
  // delegate to the real hero here.
  if (blockType === 'triveny_hero') {
    return (
      <div ref={(ref) => ref && connect(drag(ref))} style={{ position: 'relative', height: enabled ? 600 : '100vh', overflow: 'hidden' }}>
        <div style={{ pointerEvents: enabled ? 'none' : 'auto', height: '100%' }}>
          <TrivenyHero contained cycleMs={enabled ? 0 : 3600} loginUrl="#" statements={content?.statements} />
        </div>
      </div>
    );
  }
  // Public render: real media-query hide + responsive-override classes. Editor:
  // keep the block visible (selectable) but dim it + badge when hidden on the
  // previewed device, and apply the device's responsive override inline.
  const hideClasses = !enabled
    ? [hideOn?.mobile && 'tv-hide-mobile', hideOn?.tablet && 'tv-hide-tablet', hideOn?.desktop && 'tv-hide-desktop'].filter(Boolean).join(' ')
    : '';
  const hasResponsive = responsive && (responsive.tablet || responsive.mobile);
  const editorHidden = enabled && !!hideOn?.[device];
  const editorOverride = enabled && device !== 'desktop' ? resStyleObj(responsive?.[device] || {}) : {};
  const wrapStyle: React.CSSProperties = { position: 'relative', opacity: editorHidden ? 0.4 : undefined, ...editorOverride };
  const className = [hideClasses, !enabled && hasResponsive ? respCls : ''].filter(Boolean).join(' ');
  return (
    <div ref={(ref) => ref && connect(drag(ref))} className={className || undefined} style={wrapStyle}>
      {!enabled && hasResponsive && <style dangerouslySetInnerHTML={{ __html: resMediaCss(respCls, {}, responsive) }} />}
      {editorHidden && (
        <span style={{ position: 'absolute', top: 6, right: 6, zIndex: 2, background: '#14171c', color: '#fff', fontSize: 10, fontWeight: 600, padding: '2px 7px', borderRadius: 999 }}>
          Hidden on {device}
        </span>
      )}
      <div style={{ pointerEvents: enabled ? 'none' : 'auto' }}>
        <Animated animation={animation} play={!enabled}>
          <PublicSiteBlock block={{ blockType, content, styles }} t={theme} />
        </Animated>
      </div>
    </div>
  );
};
const STRUCTURED = new Set(['features', 'stats', 'testimonials', 'faq', 'pricing', 'gallery']);

// Per-item field schema for structured blocks → drives the list editor below.
const ITEM_SCHEMA: Record<string, { key: string; def: any; fields: { k: string; label: string; area?: boolean }[] }> = {
  features: { key: 'features', def: { icon: '✨', title: 'Title', description: 'Describe this.' }, fields: [{ k: 'icon', label: 'Icon (emoji)' }, { k: 'title', label: 'Title' }, { k: 'description', label: 'Description', area: true }] },
  stats: { key: 'stats', def: { value: '00', label: 'Label' }, fields: [{ k: 'value', label: 'Value' }, { k: 'label', label: 'Label' }] },
  testimonials: { key: 'testimonials', def: { quote: 'Great experience.', author: 'Name', role: 'Role', company: 'Company' }, fields: [{ k: 'quote', label: 'Quote', area: true }, { k: 'author', label: 'Author' }, { k: 'role', label: 'Role' }, { k: 'company', label: 'Company' }] },
  faq: { key: 'faqs', def: { question: 'Question?', answer: 'Answer.' }, fields: [{ k: 'question', label: 'Question' }, { k: 'answer', label: 'Answer', area: true }] },
  gallery: { key: 'images', def: { imageUrl: '', imageAlt: '' }, fields: [{ k: 'imageUrl', label: 'Image URL' }, { k: 'imageAlt', label: 'Alt text' }] },
  pricing: { key: 'plans', def: { name: 'Plan', price: '$0', period: 'mo', features: [], ctaText: 'Choose', ctaUrl: '#' }, fields: [{ k: 'name', label: 'Name' }, { k: 'price', label: 'Price' }, { k: 'period', label: 'Period' }, { k: 'ctaText', label: 'CTA text' }, { k: 'ctaUrl', label: 'CTA URL' }] },
};

const iconBtn: React.CSSProperties = { width: 22, height: 22, borderRadius: 5, border: '1px solid #e6e8ec', background: '#fff', cursor: 'pointer', fontSize: 11, color: '#6b7280', lineHeight: 1 };

// Per-item editor for structured blocks (testimonials, features, …): dedicated
// fields per item + add / remove / reorder — instead of raw JSON.
function StructuredItemsEditor({ blockType, content, setProp }: { blockType: string; content: any; setProp: (cb: (p: any) => void) => void }) {
  const schema = ITEM_SCHEMA[blockType];
  if (!schema) return null;
  const items: any[] = Array.isArray(content[schema.key]) ? content[schema.key] : [];
  const write = (next: any[]) => setProp((p: any) => { p.content = { ...p.content, [schema.key]: next }; });
  const update = (i: number, k: string, v: any) => { const n = items.slice(); n[i] = { ...n[i], [k]: v }; write(n); };
  const move = (i: number, d: number) => { const j = i + d; if (j < 0 || j >= items.length) return; const n = items.slice(); [n[i], n[j]] = [n[j], n[i]]; write(n); };
  return (
    <div style={{ display: 'grid', gap: 8 }}>
      <div style={{ fontSize: 11, color: '#9aa3af', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{schema.key} ({items.length})</div>
      {items.map((it, i) => (
        <div key={i} style={{ border: '1px solid #e6e8ec', borderRadius: 8, padding: 8, display: 'grid', gap: 6, background: '#fafafa' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontSize: 11, color: '#9aa3af' }}>#{i + 1}</span>
            <span style={{ display: 'flex', gap: 4 }}>
              <button title="Move up" style={iconBtn} onClick={() => move(i, -1)}>↑</button>
              <button title="Move down" style={iconBtn} onClick={() => move(i, 1)}>↓</button>
              <button title="Remove" style={{ ...iconBtn, color: '#dc2626', borderColor: '#fca5a5' }} onClick={() => write(items.filter((_, j) => j !== i))}>✕</button>
            </span>
          </div>
          {schema.fields.map((f) => f.area ? (
            <textarea key={f.k} placeholder={f.label} rows={2} style={cssToStyle(field)} value={it[f.k] ?? ''} onChange={(e) => update(i, f.k, e.target.value)} />
          ) : f.k === 'icon' ? (
            <div key={f.k} style={{ display: 'grid', gap: 4 }}>
              <input placeholder={f.label} style={cssToStyle(field)} value={it[f.k] ?? ''} onChange={(e) => update(i, f.k, e.target.value)} />
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
                {EMOJI_ICONS.map((em) => (
                  <button key={em} type="button" title={em} onClick={() => update(i, 'icon', em)} style={{ width: 24, height: 24, borderRadius: 5, border: it.icon === em ? '2px solid #7468ae' : '1px solid #e6e8ec', background: '#fff', cursor: 'pointer', fontSize: 14, lineHeight: 1, padding: 0 }}>{em}</button>
                ))}
              </div>
            </div>
          ) : (
            <input key={f.k} placeholder={f.label} style={cssToStyle(field)} value={it[f.k] ?? ''} onChange={(e) => update(i, f.k, e.target.value)} />
          ))}
          {blockType === 'pricing' && (
            <textarea
              placeholder="Features (one per line)"
              rows={3}
              style={cssToStyle(field)}
              value={Array.isArray(it.features) ? it.features.join('\n') : ''}
              onChange={(e) => update(i, 'features', e.target.value.split('\n').map((s) => s.trim()).filter(Boolean))}
            />
          )}
        </div>
      ))}
      <button
        onClick={() => write([...items, JSON.parse(JSON.stringify(schema.def))])}
        style={{ padding: '7px 10px', borderRadius: 7, border: '1px dashed #c7b8f0', background: '#f7f4ff', color: '#5b4b9e', fontSize: 12.5, fontWeight: 600, cursor: 'pointer' }}
      >
        + Add item
      </button>
    </div>
  );
}

// Form block picker — lists the tenant's Web Presence forms and snapshots the
// selected form's fields into the block content so it renders in-canvas and on
// the public site without a live fetch. Submission (public) resolves the form
// by id + host at runtime.
const FormPicker = ({ content, set }: { content: any; set: (patch: any) => void }) => {
  const [forms, setForms] = useState<any[] | null>(null);
  useEffect(() => {
    webPresenceApi.getForms().then((r: any) => {
      const rows = r?.data ?? r ?? [];
      setForms(Array.isArray(rows) ? rows : []);
    }).catch(() => setForms([]));
  }, []);
  const pick = (id: string) => {
    const f = (forms || []).find((x) => String(x.id) === id);
    if (!f) { set({ formId: '', formName: '', fields: [] }); return; }
    let fields = f.fields;
    if (typeof fields === 'string') { try { fields = JSON.parse(fields); } catch { fields = []; } }
    set({ formId: String(f.id), formName: f.form_name || f.name || 'Form', fields: Array.isArray(fields) ? fields : [], successMessage: f.success_message || '' });
  };
  return (
    <label style={{ fontSize: 12, color: '#6b7280' }}>Form
      <select style={cssToStyle(field)} value={content.formId || ''} onChange={(e) => pick(e.target.value)}>
        <option value="">{forms === null ? 'Loading…' : 'Select a form…'}</option>
        {(forms || []).map((f) => (
          <option key={f.id} value={String(f.id)}>{f.form_name || f.name || 'Untitled form'}</option>
        ))}
      </select>
      {content.formId && <span style={{ fontSize: 11, color: '#16a34a', display: 'block', marginTop: 4 }}>{(content.fields || []).length} field(s) embedded · {content.formName}</span>}
      {forms !== null && forms.length === 0 && <span style={{ fontSize: 11, color: '#9aa3af', display: 'block', marginTop: 4 }}>No forms yet — create one in the Forms tab.</span>}
    </label>
  );
};

const BlockSettings = () => {
  const { props, actions: { setProp } } = useNode((node) => ({ props: node.data.props }));
  const brand = useContext(BrandCtx);
  // Brand swatches (named) come first so a user can explicitly pick a brand
  // color per block; then the org's design tokens; then the generic palette.
  const brandSwatches = (
    [
      ['Brand primary', brand.primary],
      ['Brand accent', brand.accent],
      ['Brand text', brand.text],
      ['Brand background', brand.background],
      ...((brand.tokens || [])
        .filter((t) => (t.group || 'color') === 'color' && /^#|rgb|hsl/i.test(t.value || ''))
        .map((t) => [t.name, t.value] as [string, string | undefined])),
    ] as Array<[string, string | undefined]>
  )
    .filter(([, v]) => !!v)
    .filter(([, v], i, arr) => arr.findIndex(([, w]) => (w || '').toLowerCase() === (v || '').toLowerCase()) === i) as Array<[string, string]>;
  const bt = props.blockType as string;
  const c = props.content || {};
  const set = (patch: any) => setProp((p: any) => { p.content = { ...p.content, ...patch }; });
  const st = props.styles || {};
  const setStyle = (patch: any) => setProp((p: any) => { p.styles = { ...(p.styles || {}), ...patch }; });
  const simple = (label: string, key: string, ph = '') => (
    <label style={{ fontSize: 12, color: '#6b7280' }}>{label}
      <input style={cssToStyle(field)} type="text" value={c[key] ?? ''} placeholder={ph} onChange={(e) => set({ [key]: e.target.value })} />
    </label>
  );
  // Color control: full spectrum picker + a swatch palette + a hex/CSS field.
  const colorRow = (label: string, key: string) => (
    <label style={{ fontSize: 12, color: '#6b7280' }}>{label}
      <span style={{ display: 'flex', gap: 6, marginTop: 4 }}>
        <input type="color" title="Pick from the full spectrum" value={/^#[0-9a-fA-F]{6}$/.test(st[key] || '') ? st[key] : '#ffffff'} onChange={(e) => setStyle({ [key]: e.target.value })} style={{ width: 40, height: 32, padding: 0, border: '1px solid #d7dbe0', borderRadius: 6, background: '#fff', cursor: 'pointer' }} />
        <input style={{ ...cssToStyle(field), marginTop: 0, flex: 1 }} type="text" value={st[key] ?? ''} placeholder="inherit / #hex / css color" onChange={(e) => setStyle({ [key]: e.target.value || undefined })} />
      </span>
      {brandSwatches.length > 0 && (
        <span style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 6, alignItems: 'center' }}>
          <span style={{ fontSize: 10, color: '#9aa3af', marginRight: 2 }}>Brand</span>
          {brandSwatches.map(([name, sw]) => (
            <button
              key={name}
              type="button"
              title={`${name} (${sw})`}
              onClick={() => setStyle({ [key]: sw })}
              style={{ width: 20, height: 20, borderRadius: 4, background: sw, cursor: 'pointer', border: (st[key] || '').toLowerCase() === sw.toLowerCase() ? '2px solid #14171c' : '2px solid #c7b8f0' }}
            />
          ))}
        </span>
      )}
      <span style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 6 }}>
        {SWATCHES.map((sw) => (
          <button
            key={sw}
            type="button"
            title={sw}
            onClick={() => setStyle({ [key]: sw })}
            style={{ width: 20, height: 20, borderRadius: 4, background: sw, cursor: 'pointer', border: (st[key] || '').toLowerCase() === sw ? '2px solid #14171c' : '1px solid #d7dbe0' }}
          />
        ))}
      </span>
    </label>
  );
  return (
    <div style={{ display: 'grid', gap: 10 }}>
      <div style={{ fontSize: 11, color: '#9aa3af', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{bt}</div>
      {bt === 'hero' && (<>{simple('Headline', 'headline')}{simple('Subheadline', 'subheadline')}</>)}
      {bt === 'heading' && (<>{simple('Text', 'text')}{simple('Level (1-6)', 'level')}</>)}
      {bt === 'cta' && (<>{simple('Title', 'title')}{simple('Description', 'description')}</>)}
      {bt === 'button' && (<>{simple('Text', 'text')}{simple('URL', 'url')}</>)}
      {bt === 'text' && (
        <label style={{ fontSize: 12, color: '#6b7280' }}>Text
          <textarea style={cssToStyle(field)} rows={4} value={c.text ?? ''} onChange={(e) => set({ text: e.target.value })} />
        </label>
      )}
      {bt === 'image' && (<><ImageUpload label="Image" value={c.imageUrl || ''} onChange={(v) => set({ imageUrl: v })} />{simple('Caption', 'caption')}</>)}
      {bt === 'video' && simple('Video URL', 'videoUrl', 'https://youtube.com/…')}
      {bt === 'form' && (<>{simple('Heading', 'title')}{simple('Description', 'description')}{simple('Submit button label', 'submitLabel')}<FormPicker content={c} set={set} /></>)}
      {STRUCTURED.has(bt) && (
        <>
          {(bt === 'features' || bt === 'faq' || bt === 'pricing') && simple('Section title', 'title')}
          <StructuredItemsEditor blockType={bt} content={c} setProp={setProp} />
          <details>
            <summary style={{ fontSize: 11, color: '#9aa3af', cursor: 'pointer' }}>Advanced (raw JSON)</summary>
            <textarea
              style={{ ...cssToStyle(field), fontFamily: 'monospace', fontSize: 11 }}
              rows={8}
              defaultValue={JSON.stringify(c, null, 2)}
              onBlur={(e) => { try { setProp((p: any) => (p.content = JSON.parse(e.target.value))); } catch { /* ignore bad json */ } }}
            />
          </details>
        </>
      )}
      {/* Manual styling — color/spacing without AI (applied to the block via
          the same renderer the public site uses). */}
      <div style={{ display: 'grid', gap: 8, borderTop: '1px dashed #e6e8ec', paddingTop: 10, marginTop: 4 }}>
        <div style={{ fontSize: 11, color: '#9aa3af', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Style</div>
        {colorRow('Background', 'backgroundColor')}
        {colorRow('Text color', 'color')}
        <label style={{ fontSize: 12, color: '#6b7280' }}>Padding (CSS)
          <input style={cssToStyle(field)} type="text" value={st.padding ?? ''} placeholder="e.g. 48px 24px" onChange={(e) => setStyle({ padding: e.target.value || undefined })} />
        </label>
        {/* Gradient background — writes styles.backgroundGradient (toCss → background). */}
        {(() => {
          const m = /linear-gradient\(\s*(-?\d+)deg\s*,\s*([^,]+),\s*([^)]+)\)/.exec(st.backgroundGradient || '');
          const gA = m ? Number(m[1]) : 135;
          const gF = m ? m[2].trim() : '#7468ae';
          const gT = m ? m[3].trim() : '#46b1e5';
          const build = (a: number, f: string, t: string) => setStyle({ backgroundGradient: `linear-gradient(${a}deg, ${f}, ${t})` });
          const sw: React.CSSProperties = { width: 34, height: 30, padding: 0, border: '1px solid #d7dbe0', borderRadius: 6, background: '#fff', cursor: 'pointer' };
          return (
            <div style={{ display: 'grid', gap: 6 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <span style={{ fontSize: 12, color: '#6b7280' }}>Gradient</span>
                {st.backgroundGradient && <button onClick={() => setStyle({ backgroundGradient: undefined })} style={{ fontSize: 11, color: '#9aa3af', background: 'none', border: 'none', cursor: 'pointer' }}>Clear</button>}
              </div>
              <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                <input type="color" title="From" value={/^#[0-9a-fA-F]{6}$/.test(gF) ? gF : '#7468ae'} onChange={(e) => build(gA, e.target.value, gT)} style={sw} />
                <input type="color" title="To" value={/^#[0-9a-fA-F]{6}$/.test(gT) ? gT : '#46b1e5'} onChange={(e) => build(gA, gF, e.target.value)} style={sw} />
                <input type="number" title="Angle" value={gA} onChange={(e) => build(Number(e.target.value), gF, gT)} style={{ ...cssToStyle(field), marginTop: 0, width: 62 }} />
                <span style={{ fontSize: 11, color: '#9aa3af' }}>deg</span>
              </div>
            </div>
          );
        })()}
        {/* Shadow presets — writes styles.boxShadow (passes through toCss). */}
        {(() => {
          const PRESETS: Record<string, string> = { none: '', sm: '0 1px 2px rgba(20,23,28,0.08)', md: '0 8px 24px -10px rgba(20,23,28,0.18)', lg: '0 24px 60px -16px rgba(20,23,28,0.28)' };
          const cur = Object.keys(PRESETS).find((k) => PRESETS[k] === (st.boxShadow || '')) ?? 'custom';
          return (
            <label style={{ fontSize: 12, color: '#6b7280' }}>Shadow
              <select style={cssToStyle(field)} value={cur} onChange={(e) => { const v = e.target.value; if (v !== 'custom') setStyle({ boxShadow: PRESETS[v] || undefined }); }}>
                <option value="none">None</option>
                <option value="sm">Small</option>
                <option value="md">Medium</option>
                <option value="lg">Large</option>
                {cur === 'custom' && <option value="custom">Custom</option>}
              </select>
            </label>
          );
        })()}
        <label style={{ fontSize: 12, color: '#6b7280' }}>Corner radius (px)
          <input style={cssToStyle(field)} type="number" value={parseInt(st.borderRadius, 10) || 0} onChange={(e) => setStyle({ borderRadius: e.target.value ? Number(e.target.value) : undefined })} />
        </label>
      </div>
      <AnimationSettings animation={props.animation} onChange={(a) => setProp((p: any) => (p.animation = a))} />
      <div style={{ display: 'grid', gap: 6, borderTop: '1px dashed #e6e8ec', paddingTop: 10, marginTop: 4 }}>
        <div style={{ fontSize: 11, color: '#9aa3af', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Visibility</div>
        {(['desktop', 'tablet', 'mobile'] as const).map((d) => (
          <label key={d} style={{ fontSize: 12, color: '#6b7280', display: 'flex', alignItems: 'center', gap: 8, textTransform: 'capitalize' }}>
            <input
              type="checkbox"
              checked={!!(props.hideOn && props.hideOn[d])}
              onChange={(e) => setProp((p: any) => { p.hideOn = { ...(p.hideOn || {}), [d]: e.target.checked }; })}
            />
            Hide on {d}
          </label>
        ))}
      </div>
      <ResponsiveOverridesEditor responsive={props.responsive} onPatch={(bp, patch) => patchResponsive(setProp, bp, patch)} />
    </div>
  );
};
BlockNode.craft = {
  displayName: 'Block',
  props: { blockType: 'hero', content: BLOCK_DEFAULTS.hero, styles: {}, animation: { type: 'none' }, hideOn: {}, responsive: {} },
  related: { settings: BlockSettings },
};

// ---------------------------------------------------------------------------
// Free canvas + layers — the "Free/Layered" mode (vs flow/stack). Craft.js is
// flow-based, so absolute positioning is implemented with our own pointer-drag:
//   FreeContainer (position:relative, fixed height) holds FreeLayer children;
//   FreeLayer (position:absolute, x/y/z/width) holds one block and is dragged
//   by a corner handle that writes x/y back to the node. This is what enables
//   layered, Triveny-class sections (things stacked on top of each other).
// ---------------------------------------------------------------------------
// Multi-select for free layers: a container-scoped selection set so several layers
// can be shift-clicked and then moved / deleted together (Craft itself is single-select).
type MultiSel = { sel: Set<string>; setSel: (s: Set<string>) => void };
const MultiSelectCtx = React.createContext<MultiSel | null>(null);

export const FreeContainer = ({ background, height, children }: any) => {
  const { id, connectors: { connect } } = useNode();
  const { enabled, query } = useEditor((s) => ({ enabled: s.options.enabled }));
  const [sel, setSelState] = useState<Set<string>>(new Set());
  const setSel = (s: Set<string>) => setSelState(new Set(s));
  const boxRef = useRef<HTMLDivElement | null>(null);
  const [marq, setMarq] = useState<{ x0: number; y0: number; x1: number; y1: number } | null>(null);

  // Rubber-band select: press on empty canvas and drag a box; layers it touches join
  // the selection (Shift adds to the current set). A tiny press (no drag) just clears.
  const onBgDown = (e: React.PointerEvent) => {
    if (!enabled || e.target !== e.currentTarget || !boxRef.current) return;
    const rect = boxRef.current.getBoundingClientRect();
    const x0 = e.clientX - rect.left;
    const y0 = e.clientY - rect.top;
    const additive = e.shiftKey;
    const base = additive ? new Set(sel) : new Set<string>();
    setMarq({ x0, y0, x1: x0, y1: y0 });
    const move = (ev: PointerEvent) => setMarq({ x0, y0, x1: ev.clientX - rect.left, y1: ev.clientY - rect.top });
    const up = (ev: PointerEvent) => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
      setMarq(null);
      const ex = ev.clientX - rect.left;
      const ey = ev.clientY - rect.top;
      const mx0 = Math.min(x0, ex), my0 = Math.min(y0, ey), mx1 = Math.max(x0, ex), my1 = Math.max(y0, ey);
      if (mx1 - mx0 < 4 && my1 - my0 < 4) { if (!additive && sel.size) setSel(new Set()); return; }
      const next = new Set(base);
      try {
        const childIds: string[] = query.node(id).get().data.nodes || [];
        childIds.forEach((cid) => {
          const dom = query.node(cid).get().dom as HTMLElement | null;
          if (!dom) return;
          const b = dom.getBoundingClientRect();
          const cx0 = b.left - rect.left, cy0 = b.top - rect.top, cx1 = b.right - rect.left, cy1 = b.bottom - rect.top;
          if (cx0 < mx1 && cx1 > mx0 && cy0 < my1 && cy1 > my0) next.add(cid);
        });
      } catch { /* ignore */ }
      setSel(next);
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
  };

  return (
    <MultiSelectCtx.Provider value={{ sel, setSel }}>
      <div
        ref={(ref) => { boxRef.current = ref; if (ref) connect(ref); }}
        onPointerDown={onBgDown}
        style={{ position: 'relative', height: height || 480, background: background || '#ffffff', overflow: 'hidden' }}
      >
        {children}
        {marq && (
          <div style={{ position: 'absolute', left: Math.min(marq.x0, marq.x1), top: Math.min(marq.y0, marq.y1), width: Math.abs(marq.x1 - marq.x0), height: Math.abs(marq.y1 - marq.y0), border: '1px solid #7468ae', background: 'rgba(116,104,174,0.12)', pointerEvents: 'none', zIndex: 6 }} />
        )}
      </div>
    </MultiSelectCtx.Provider>
  );
};
const FreeContainerSettings = () => {
  const { props, actions: { setProp } } = useNode((node) => ({ props: node.data.props }));
  return (
    <div style={{ display: 'grid', gap: 10 }}>
      <p style={{ fontSize: 11, color: '#9aa3af', margin: 0 }}>Free canvas — drop “Layer” blocks inside and drag them to position; set z-index per layer to stack.</p>
      <label style={{ fontSize: 12, color: '#6b7280' }}>Background
        <input style={cssToStyle(field)} type="text" value={props.background || ''} onChange={(e) => setProp((p: any) => (p.background = e.target.value))} />
      </label>
      <label style={{ fontSize: 12, color: '#6b7280' }}>Height (px)
        <input style={cssToStyle(field)} type="number" value={parseInt(props.height) || 480} onChange={(e) => setProp((p: any) => (p.height = Number(e.target.value)))} />
      </label>
    </div>
  );
};
FreeContainer.craft = {
  displayName: 'Free Canvas',
  props: { background: '#ffffff', height: 480 },
  rules: { canMoveIn: () => true },
  related: { settings: FreeContainerSettings },
};

// Free-canvas grid step — drag snaps to this; arrow keys nudge 1px, Shift+arrow one step.
const GRID = 8;
const snap = (v: number) => Math.round(v / GRID) * GRID;

export const FreeLayer = ({ x, y, z, width, height, children }: any) => {
  const { id, connectors: { connect }, actions: { setProp }, selected } = useNode((node) => ({ selected: node.events.selected }));
  const { enabled, actions: edActions, query } = useEditor((state) => ({ enabled: state.options.enabled }));
  const ms = useContext(MultiSelectCtx);
  const inSel = ms?.sel.has(id) ?? false;
  const ref = useRef<HTMLDivElement | null>(null);

  const toggleSel = () => {
    if (!ms) return;
    const s = new Set(ms.sel);
    s.has(id) ? s.delete(id) : s.add(id);
    ms.setSel(s);
  };

  // Shared pointer-drag helper: track from a start point and write deltas back.
  const startDrag = (e: React.PointerEvent, onDelta: (dx: number, dy: number) => void) => {
    if (!enabled) return;
    e.stopPropagation();
    e.preventDefault();
    const sx = e.clientX;
    const sy = e.clientY;
    const move = (ev: PointerEvent) => onDelta(ev.clientX - sx, ev.clientY - sy);
    const up = () => { window.removeEventListener('pointermove', move); window.removeEventListener('pointerup', up); };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
  };
  const onMoveDown = (e: React.PointerEvent) => {
    if (e.shiftKey) { e.stopPropagation(); e.preventDefault(); toggleSel(); return; }
    // Group move when this layer is part of a multi-selection; else move just this one.
    const groupIds = ms && ms.sel.has(id) && ms.sel.size > 1 ? Array.from(ms.sel) : [id];
    const starts = new Map<string, { x: number; y: number }>();
    groupIds.forEach((gid) => {
      const pr = (() => { try { return query.node(gid).get().data.props; } catch { return {}; } })();
      starts.set(gid, { x: pr.x ?? 20, y: pr.y ?? 20 });
    });
    startDrag(e, (dx, dy) => groupIds.forEach((gid) => {
      const st = starts.get(gid)!;
      edActions.setProp(gid, (p: any) => { p.x = Math.max(0, snap(st.x + dx)); p.y = Math.max(0, snap(st.y + dy)); });
    }));
  };
  const onResizeDown = (e: React.PointerEvent) => {
    const ow = typeof width === 'number' ? width : parseInt(width, 10) || 320;
    const oh = typeof height === 'number' ? height : parseInt(height, 10) || (ref.current?.offsetHeight ?? 120);
    startDrag(e, (dx, dy) => setProp((p: any) => { p.width = Math.max(24, snap(ow + dx)); p.height = Math.max(24, snap(oh + dy)); }));
  };

  // Keyboard shortcuts while selected: arrow nudge (Shift = grid step), Delete to
  // remove, [ / ] to send backward / bring forward (z-order).
  useEffect(() => {
    if (!enabled || !selected) return;
    const onKey = (e: KeyboardEvent) => {
      const tag = (e.target as HTMLElement)?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || (e.target as HTMLElement)?.isContentEditable) return;
      const map: Record<string, [number, number]> = { ArrowLeft: [-1, 0], ArrowRight: [1, 0], ArrowUp: [0, -1], ArrowDown: [0, 1] };
      const d = map[e.key];
      if (d) {
        e.preventDefault();
        const step = e.shiftKey ? GRID : 1;
        setProp((p: any) => { p.x = Math.max(0, (p.x ?? 20) + d[0] * step); p.y = Math.max(0, (p.y ?? 20) + d[1] * step); });
        return;
      }
      if (e.key === 'Delete' || e.key === 'Backspace') {
        e.preventDefault();
        const ids = ms && ms.sel.has(id) && ms.sel.size > 1 ? Array.from(ms.sel) : [id];
        ids.forEach((gid) => { try { if (query.node(gid).isDeletable()) edActions.delete(gid); } catch { /* ignore */ } });
        if (ms && ids.length > 1) ms.setSel(new Set());
        return;
      }
      if (e.key === ']') { e.preventDefault(); setProp((p: any) => (p.z = (p.z ?? 1) + 1)); return; }
      if (e.key === '[') { e.preventDefault(); setProp((p: any) => (p.z = Math.max(0, (p.z ?? 1) - 1))); return; }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [enabled, selected, setProp, id, edActions, query, ms]);

  // Align within the parent free canvas (reads live parent + layer dimensions).
  const align = (hz: 'l' | 'c' | 'r' | null, vt: 't' | 'm' | 'b' | null) => {
    const el = ref.current;
    const parent = el?.offsetParent as HTMLElement | null;
    if (!el || !parent) return;
    const pw = parent.clientWidth; const ph = parent.clientHeight;
    const lw = el.offsetWidth; const lh = el.offsetHeight;
    setProp((p: any) => {
      if (hz === 'l') p.x = 0; else if (hz === 'c') p.x = Math.max(0, Math.round((pw - lw) / 2)); else if (hz === 'r') p.x = Math.max(0, pw - lw);
      if (vt === 't') p.y = 0; else if (vt === 'm') p.y = Math.max(0, Math.round((ph - lh) / 2)); else if (vt === 'b') p.y = Math.max(0, ph - lh);
    });
  };
  const alignBtn = (label: string, title: string, fn: () => void) => (
    <button onPointerDown={(e) => { e.stopPropagation(); e.preventDefault(); fn(); }} title={title}
      style={{ width: 20, height: 20, borderRadius: 4, border: 'none', background: 'transparent', color: '#fff', cursor: 'pointer', fontSize: 12, lineHeight: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{label}</button>
  );

  return (
    <div
      ref={(r) => { ref.current = r; if (r) connect(r); }}
      onClickCapture={(e) => { if (enabled && e.shiftKey) { e.stopPropagation(); e.preventDefault(); toggleSel(); } }}
      style={{ position: 'absolute', left: x ?? 20, top: y ?? 20, zIndex: z ?? 1, width: width || 320, height: height || undefined, outline: inSel ? '2px solid #7468ae' : undefined, outlineOffset: 2 }}
    >
      {enabled && (
        <>
          <div
            onPointerDown={onMoveDown}
            title={inSel ? 'Drag to move the selection · Shift-click to toggle' : 'Drag to move · Shift-click to multi-select · arrow keys nudge'}
            style={{ position: 'absolute', top: -10, left: -10, width: 22, height: 22, borderRadius: 6, background: '#14171c', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'grab', fontSize: 12, zIndex: 3, userSelect: 'none' }}
          >
            ✥
          </div>
          {selected && (
            <div style={{ position: 'absolute', top: -34, left: 16, display: 'flex', gap: 1, background: '#14171c', borderRadius: 6, padding: '2px 3px', zIndex: 4 }}>
              {alignBtn('⭰', 'Align left', () => align('l', null))}
              {alignBtn('⭤', 'Center horizontally', () => align('c', null))}
              {alignBtn('⭲', 'Align right', () => align('r', null))}
              <span style={{ width: 1, background: '#3a3f4a', margin: '2px 2px' }} />
              {alignBtn('⭱', 'Align top', () => align(null, 't'))}
              {alignBtn('⭥', 'Center vertically', () => align(null, 'm'))}
              {alignBtn('⭳', 'Align bottom', () => align(null, 'b'))}
            </div>
          )}
          <div
            onPointerDown={onResizeDown}
            title="Drag to resize"
            style={{ position: 'absolute', bottom: -7, right: -7, width: 14, height: 14, borderRadius: 3, background: '#fff', border: '2px solid #7468ae', cursor: 'nwse-resize', zIndex: 3, userSelect: 'none' }}
          />
        </>
      )}
      {children}
    </div>
  );
};
const FreeLayerSettings = () => {
  const { props, actions: { setProp } } = useNode((node) => ({ props: node.data.props }));
  const num = (label: string, key: string) => (
    <label style={{ fontSize: 12, color: '#6b7280', flex: 1 }}>{label}
      <input style={cssToStyle(field)} type="number" value={props[key] ?? 0} onChange={(e) => setProp((p: any) => (p[key] = Number(e.target.value)))} />
    </label>
  );
  return (
    <div style={{ display: 'grid', gap: 10 }}>
      <p style={{ fontSize: 11, color: '#9aa3af', margin: 0 }}>Drag the ✥ handle to move and the corner handle to resize, or set values. z-index controls stacking.</p>
      <div style={{ display: 'flex', gap: 8 }}>{num('X', 'x')}{num('Y', 'y')}</div>
      <div style={{ display: 'flex', gap: 8 }}>{num('Width', 'width')}{num('Height', 'height')}</div>
      <div style={{ display: 'flex', gap: 8 }}>{num('z-index', 'z')}<span style={{ flex: 1 }} /></div>
    </div>
  );
};
FreeLayer.craft = {
  displayName: 'Layer',
  props: { x: 24, y: 24, z: 1, width: 360 },
  rules: { canMoveIn: () => true },
  related: { settings: FreeLayerSettings },
};

// ---------------------------------------------------------------------------
// Shape primitives — rectangle / ellipse / line. The basic vector objects Paulo
// listed from Figma's toolbar. Drop one on the canvas (or inside a Free Layer to
// position it freely) as a fill/overlay/divider.
// ---------------------------------------------------------------------------
export const ShapeNode = ({ shape, fill, radius, borderColor, borderWidth, width, height, opacity }: any) => {
  const { connectors: { connect, drag } } = useNode();
  const w = width || '100%';
  const common: React.CSSProperties = {
    width: w,
    opacity: opacity ?? 1,
    border: borderWidth ? `${borderWidth}px solid ${borderColor || '#14171c'}` : 'none',
  };
  let el: React.ReactNode;
  if (shape === 'line') {
    el = <div style={{ ...common, height: borderWidth || 2, background: fill || '#14171c', border: 'none' }} />;
  } else {
    el = (
      <div
        style={{
          ...common,
          height: height || 120,
          background: fill || '#e9e3f6',
          borderRadius: shape === 'ellipse' ? '50%' : (radius ?? 8),
        }}
      />
    );
  }
  return <div ref={(ref) => ref && connect(drag(ref))} style={{ display: 'block' }}>{el}</div>;
};
const ShapeSettings = () => {
  const { props, actions: { setProp } } = useNode((node) => ({ props: node.data.props }));
  const isLine = props.shape === 'line';
  return (
    <div style={{ display: 'grid', gap: 10 }}>
      <label style={{ fontSize: 12, color: '#6b7280' }}>Shape
        <select style={cssToStyle(field)} value={props.shape || 'rectangle'} onChange={(e) => setProp((p: any) => (p.shape = e.target.value))}>
          <option value="rectangle">Rectangle</option>
          <option value="ellipse">Ellipse</option>
          <option value="line">Line</option>
        </select>
      </label>
      <label style={{ fontSize: 12, color: '#6b7280' }}>{isLine ? 'Color' : 'Fill'}
        <input style={cssToStyle(field)} type="text" value={props.fill || ''} onChange={(e) => setProp((p: any) => (p.fill = e.target.value))} placeholder="#e9e3f6 or rgba(...)" />
      </label>
      <div style={{ display: 'flex', gap: 8 }}>
        <label style={{ fontSize: 12, color: '#6b7280', flex: 1 }}>Width (CSS)
          <input style={cssToStyle(field)} type="text" value={props.width || ''} onChange={(e) => setProp((p: any) => (p.width = e.target.value))} placeholder="100% / 240px" />
        </label>
        {!isLine && (
          <label style={{ fontSize: 12, color: '#6b7280', flex: 1 }}>Height (px)
            <input style={cssToStyle(field)} type="number" value={props.height || 0} onChange={(e) => setProp((p: any) => (p.height = Number(e.target.value)))} />
          </label>
        )}
      </div>
      {isLine ? (
        <label style={{ fontSize: 12, color: '#6b7280' }}>Thickness (px)
          <input style={cssToStyle(field)} type="number" value={props.borderWidth || 2} onChange={(e) => setProp((p: any) => (p.borderWidth = Number(e.target.value)))} />
        </label>
      ) : (
        <div style={{ display: 'flex', gap: 8 }}>
          <label style={{ fontSize: 12, color: '#6b7280', flex: 1 }}>Corner radius
            <input style={cssToStyle(field)} type="number" value={props.radius ?? 8} onChange={(e) => setProp((p: any) => (p.radius = Number(e.target.value)))} />
          </label>
          <label style={{ fontSize: 12, color: '#6b7280', flex: 1 }}>Opacity
            <input style={cssToStyle(field)} type="number" step="0.05" min="0" max="1" value={props.opacity ?? 1} onChange={(e) => setProp((p: any) => (p.opacity = Number(e.target.value)))} />
          </label>
        </div>
      )}
    </div>
  );
};
ShapeNode.craft = {
  displayName: 'Shape',
  props: { shape: 'rectangle', fill: '#e9e3f6', radius: 8, borderColor: '#14171c', borderWidth: 0, width: '100%', height: 120, opacity: 1 },
  related: { settings: ShapeSettings },
};

// ---------------------------------------------------------------------------
// SVG / logo — bring a vector asset onto the canvas: paste SVG code (e.g. a Figma
// export or an icon) or point at an image URL. Scales crisply; drop inside a Free
// Layer to place and resize it. This is the first step of vector/logo authoring.
// ---------------------------------------------------------------------------
// Strip any fixed width/height off the pasted <svg> so it scales to the wrapper,
// and default its fill to currentColor so the color control below applies.
function _fitSvg(svg: string): string {
  return (svg || '').replace(/<svg([^>]*)>/i, (_m, attrs) => {
    const cleaned = String(attrs).replace(/\s(width|height)="[^"]*"/gi, '');
    return `<svg${cleaned} style="width:100%;height:auto;display:block" fill="currentColor">`;
  });
}
export const SvgNode = ({ svg, src, width, color }: any) => {
  const { connectors: { connect, drag } } = useNode();
  const w = width || 160;
  let inner: React.ReactNode;
  if (svg && svg.trim()) {
    inner = <div style={{ width: w, color: color || undefined, lineHeight: 0 }} dangerouslySetInnerHTML={{ __html: _fitSvg(svg) }} />;
  } else if (src) {
    inner = <img src={src} alt="" style={{ width: w, display: 'block' }} />;
  } else {
    inner = <div style={{ width: w, minHeight: 60, border: '1px dashed #d7dbe0', borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#9aa3af', fontSize: 12, textAlign: 'center', padding: 8 }}>SVG / logo — paste code or a URL in the panel →</div>;
  }
  return <div ref={(r) => r && connect(drag(r))} style={{ display: 'inline-block' }}>{inner}</div>;
};
const SvgSettings = () => {
  const { props, actions: { setProp } } = useNode((node) => ({ props: node.data.props }));
  return (
    <div style={{ display: 'grid', gap: 10 }}>
      <p style={{ fontSize: 11, color: '#9aa3af', margin: 0 }}>Paste SVG code (from Figma / an icon set) or set an image URL. SVG scales crisply; the color applies when the SVG uses currentColor.</p>
      <label style={{ fontSize: 12, color: '#6b7280' }}>SVG code
        <textarea style={{ ...cssToStyle(field), fontFamily: 'monospace', fontSize: 11, minHeight: 90 }} value={props.svg || ''} onChange={(e) => setProp((p: any) => (p.svg = e.target.value))} placeholder="<svg viewBox='0 0 24 24'>…</svg>" />
      </label>
      <label style={{ fontSize: 12, color: '#6b7280' }}>…or image URL
        <input style={cssToStyle(field)} type="text" value={props.src || ''} onChange={(e) => setProp((p: any) => (p.src = e.target.value))} placeholder="https://…/logo.svg" />
      </label>
      <div style={{ display: 'flex', gap: 8 }}>
        <label style={{ fontSize: 12, color: '#6b7280', flex: 1 }}>Width (px)
          <input style={cssToStyle(field)} type="number" value={parseInt(props.width, 10) || 160} onChange={(e) => setProp((p: any) => (p.width = Number(e.target.value)))} />
        </label>
        <label style={{ fontSize: 12, color: '#6b7280', flex: 1 }}>Color
          <input style={cssToStyle(field)} type="text" value={props.color || ''} onChange={(e) => setProp((p: any) => (p.color = e.target.value || undefined))} placeholder="#14171c" />
        </label>
      </div>
    </div>
  );
};
SvgNode.craft = {
  displayName: 'SVG',
  props: { svg: '', src: '', width: 160, color: '' },
  related: { settings: SvgSettings },
};

// ---------------------------------------------------------------------------
// Path — draw a vector shape by points. Editable anchors when selected: drag a
// point to move it, double-click the canvas to add one, right-click an anchor to
// remove it. Open (polyline) or closed (polygon). The authoring step beyond
// placing existing SVG; bezier curve handles are a later add.
// ---------------------------------------------------------------------------
// A point with an optional mirrored bezier handle (hx,hy = the OUT control offset;
// the IN control is its mirror, so segments stay smooth). No handle → straight.
type Pt = { x: number; y: number; hx?: number; hy?: number };
// Build a cubic <path>: each segment's out-control = prev + handle, in-control =
// next − handle. With zero handles this collapses to straight lines.
export function buildPathD(pts: Pt[], closed: boolean): string {
  if (pts.length < 2) return '';
  const seg = (a: Pt, b: Pt) => ` C ${a.x + (a.hx || 0)} ${a.y + (a.hy || 0)} ${b.x - (b.hx || 0)} ${b.y - (b.hy || 0)} ${b.x} ${b.y}`;
  let d = `M ${pts[0].x} ${pts[0].y}`;
  for (let i = 1; i < pts.length; i++) d += seg(pts[i - 1], pts[i]);
  if (closed) d += seg(pts[pts.length - 1], pts[0]) + ' Z';
  return d;
}
export const PathNode = ({ points, closed, stroke, strokeWidth, fill, width, height }: any) => {
  const { connectors: { connect }, actions: { setProp }, selected } = useNode((n) => ({ selected: n.events.selected }));
  const { enabled } = useEditor((s) => ({ enabled: s.options.enabled }));
  const ref = useRef<HTMLDivElement | null>(null);
  const w = width || 220, h = height || 140;
  const pts: Pt[] = Array.isArray(points) && points.length >= 2 ? points : [{ x: 20, y: h - 20 }, { x: w / 2, y: 20 }, { x: w - 20, y: h - 20 }];
  const d = buildPathD(pts, !!closed);

  // Drag an anchor: plain drag moves the point; Alt/Option-drag pulls its bezier handle.
  const dragPoint = (idx: number) => (e: React.PointerEvent) => {
    if (!enabled) return;
    e.stopPropagation(); e.preventDefault();
    const handleMode = e.altKey;
    const sx = e.clientX, sy = e.clientY, p0 = pts[idx];
    const move = (ev: PointerEvent) => setProp((pr: any) => {
      const arr = (pr.points || pts).slice();
      if (handleMode) arr[idx] = { ...p0, hx: Math.round((p0.hx || 0) + ev.clientX - sx), hy: Math.round((p0.hy || 0) + ev.clientY - sy) };
      else arr[idx] = { ...p0, x: Math.round(p0.x + ev.clientX - sx), y: Math.round(p0.y + ev.clientY - sy) };
      pr.points = arr;
    });
    const up = () => { window.removeEventListener('pointermove', move); window.removeEventListener('pointerup', up); };
    window.addEventListener('pointermove', move); window.addEventListener('pointerup', up);
  };
  // Drag a control-handle dot.
  const dragHandle = (idx: number) => (e: React.PointerEvent) => {
    if (!enabled) return;
    e.stopPropagation(); e.preventDefault();
    const sx = e.clientX, sy = e.clientY, p0 = pts[idx], ohx = p0.hx || 0, ohy = p0.hy || 0;
    const move = (ev: PointerEvent) => setProp((pr: any) => { const arr = (pr.points || pts).slice(); arr[idx] = { ...p0, hx: Math.round(ohx + ev.clientX - sx), hy: Math.round(ohy + ev.clientY - sy) }; pr.points = arr; });
    const up = () => { window.removeEventListener('pointermove', move); window.removeEventListener('pointerup', up); };
    window.addEventListener('pointermove', move); window.addEventListener('pointerup', up);
  };
  const addPoint = (e: React.MouseEvent) => {
    if (!enabled || !selected) return;
    const rect = ref.current?.getBoundingClientRect(); if (!rect) return;
    setProp((pr: any) => { const arr = (pr.points || pts).slice(); arr.push({ x: Math.round(e.clientX - rect.left), y: Math.round(e.clientY - rect.top) }); pr.points = arr; });
  };
  const removePoint = (idx: number) => (e: React.MouseEvent) => {
    e.stopPropagation(); e.preventDefault();
    if (pts.length <= 2) return;
    setProp((pr: any) => { const arr = (pr.points || pts).slice(); arr.splice(idx, 1); pr.points = arr; });
  };
  const dot = (x: number, y: number, key: string, on: React.PointerEventHandler, handle: boolean, onCtx?: React.MouseEventHandler) => (
    <div key={key} onPointerDown={on} onContextMenu={onCtx}
      title={handle ? 'Drag to curve' : 'Drag to move · Alt-drag to curve · right-click to remove'}
      style={{ position: 'absolute', left: x - (handle ? 4 : 5), top: y - (handle ? 4 : 5), width: handle ? 8 : 10, height: handle ? 8 : 10, borderRadius: handle ? 2 : '50%', background: handle ? '#7468ae' : '#fff', border: `2px solid #7468ae`, cursor: 'grab', zIndex: 3 }} />
  );

  return (
    <div ref={(r) => { ref.current = r; if (r) connect(r); }} onDoubleClick={addPoint} style={{ position: 'relative', width: w, height: h, display: 'inline-block' }}>
      <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} style={{ display: 'block', overflow: 'visible' }}>
        <path d={d} fill={closed ? (fill || 'none') : 'none'} stroke={stroke || '#14171c'} strokeWidth={strokeWidth ?? 2} strokeLinejoin="round" strokeLinecap="round" />
        {enabled && selected && pts.map((p, i) => (p.hx || p.hy) ? (
          <line key={`l${i}`} x1={p.x} y1={p.y} x2={p.x + (p.hx || 0)} y2={p.y + (p.hy || 0)} stroke="#7468ae" strokeWidth={1} strokeDasharray="3 3" />
        ) : null)}
      </svg>
      {enabled && selected && pts.flatMap((p, i) => {
        const els = [dot(p.x, p.y, `a${i}`, dragPoint(i), false, removePoint(i))];
        if (p.hx || p.hy) els.push(dot(p.x + (p.hx || 0), p.y + (p.hy || 0), `h${i}`, dragHandle(i), true));
        return els;
      })}
    </div>
  );
};
const PathSettings = () => {
  const { props, actions: { setProp } } = useNode((node) => ({ props: node.data.props }));
  const w = props.width || 220, h = props.height || 140;
  const effPts: Pt[] = Array.isArray(props.points) && props.points.length >= 2 ? props.points : [{ x: 20, y: h - 20 }, { x: w / 2, y: 20 }, { x: w - 20, y: h - 20 }];
  // Bake smooth mirrored handles (Catmull-Rom tangents) into every point.
  const smooth = () => setProp((p: any) => {
    const arr = effPts; const n = arr.length; const closed = !!props.closed;
    p.points = arr.map((pt, i) => {
      const prev = arr[closed ? (i - 1 + n) % n : Math.max(0, i - 1)];
      const next = arr[closed ? (i + 1) % n : Math.min(n - 1, i + 1)];
      return { x: pt.x, y: pt.y, hx: Math.round((next.x - prev.x) / 6), hy: Math.round((next.y - prev.y) / 6) };
    });
  });
  const straighten = () => setProp((p: any) => { p.points = effPts.map((pt) => ({ x: pt.x, y: pt.y })); });
  return (
    <div style={{ display: 'grid', gap: 10 }}>
      <p style={{ fontSize: 11, color: '#9aa3af', margin: 0 }}>Drag anchors to move · Alt-drag an anchor (or drag its square handle) to curve · double-click to add · right-click to remove.</p>
      <div style={{ display: 'flex', gap: 6 }}>
        <button onClick={smooth} style={{ flex: 1, padding: '6px 8px', borderRadius: 7, border: '1px solid #c7b8f0', background: '#f7f4ff', color: '#5b4b9e', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>Smooth curve</button>
        <button onClick={straighten} style={{ flex: 1, padding: '6px 8px', borderRadius: 7, border: '1px solid #d7dbe0', background: '#fff', color: '#4a5160', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>Straighten</button>
      </div>
      <label style={{ fontSize: 12, color: '#6b7280', display: 'flex', alignItems: 'center', gap: 8 }}>
        <input type="checkbox" checked={!!props.closed} onChange={(e) => setProp((p: any) => (p.closed = e.target.checked))} /> Closed shape (fill)
      </label>
      <div style={{ display: 'flex', gap: 8 }}>
        <label style={{ fontSize: 12, color: '#6b7280', flex: 1 }}>Stroke
          <input style={cssToStyle(field)} type="text" value={props.stroke || ''} onChange={(e) => setProp((p: any) => (p.stroke = e.target.value))} placeholder="#14171c" />
        </label>
        <label style={{ fontSize: 12, color: '#6b7280', flex: 1 }}>Width
          <input style={cssToStyle(field)} type="number" value={props.strokeWidth ?? 2} onChange={(e) => setProp((p: any) => (p.strokeWidth = Number(e.target.value)))} />
        </label>
      </div>
      {props.closed && (
        <label style={{ fontSize: 12, color: '#6b7280' }}>Fill
          <input style={cssToStyle(field)} type="text" value={props.fill || ''} onChange={(e) => setProp((p: any) => (p.fill = e.target.value || 'none'))} placeholder="#e9e3f6 or none" />
        </label>
      )}
      <div style={{ display: 'flex', gap: 8 }}>
        <label style={{ fontSize: 12, color: '#6b7280', flex: 1 }}>Box width
          <input style={cssToStyle(field)} type="number" value={props.width || 220} onChange={(e) => setProp((p: any) => (p.width = Number(e.target.value)))} />
        </label>
        <label style={{ fontSize: 12, color: '#6b7280', flex: 1 }}>Box height
          <input style={cssToStyle(field)} type="number" value={props.height || 140} onChange={(e) => setProp((p: any) => (p.height = Number(e.target.value)))} />
        </label>
      </div>
    </div>
  );
};
PathNode.craft = {
  displayName: 'Path',
  props: { points: [], closed: false, stroke: '#14171c', strokeWidth: 2, fill: 'none', width: 220, height: 140 },
  related: { settings: PathSettings },
};

// ---------------------------------------------------------------------------
// Video background — a full-bleed section with a looping <video> background and
// a color overlay, and a canvas on top for content (hero text, a Free Layer,
// etc.). This is the exact hero pattern from the Figma demo: video layer +
// rectangle overlay for contrast, composited by z-order.
// ---------------------------------------------------------------------------
export const VideoBgNode = ({ videoUrl, poster, overlayColor, overlayOpacity, minHeight, children }: any) => {
  const { connectors: { connect, drag } } = useNode();
  return (
    <div ref={(ref) => ref && connect(drag(ref))} style={{ position: 'relative', minHeight: minHeight || 460, overflow: 'hidden', background: '#0b0d12' }}>
      {videoUrl ? (
        <video
          src={videoUrl}
          poster={poster || undefined}
          autoPlay
          muted
          loop
          playsInline
          style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', zIndex: 0 }}
        />
      ) : (
        <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#7b8393', fontSize: 13, zIndex: 0 }}>
          Video background — set a video URL in the panel →
        </div>
      )}
      <div style={{ position: 'absolute', inset: 0, background: overlayColor || '#000000', opacity: overlayOpacity ?? 0.45, zIndex: 1 }} />
      <div style={{ position: 'relative', zIndex: 2, minHeight: minHeight || 460 }}>{children}</div>
    </div>
  );
};
// Stock-video browser — searches Pexels through the backend proxy and drops the
// chosen clip (+ its thumbnail as poster) into the video-background node.
// Shared stock-media browser — searches Pexels (video or photo) through the backend
// proxy and hands the chosen item back. Degrades gracefully when no key is configured.
const StockPicker = ({ kind, label, onPick }: { kind: 'video' | 'image'; label: string; onPick: (it: any) => void }) => {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [items, setItems] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [enabled, setEnabled] = useState(true);
  const search = async () => {
    setLoading(true);
    try {
      const r: any = await (kind === 'video' ? webPresenceApi.stockVideos(q) : webPresenceApi.stockImages(q));
      const d = r?.data ?? r;
      setEnabled(d?.enabled !== false);
      setItems(Array.isArray(d?.items) ? d.items : []);
    } catch { setItems([]); } finally { setLoading(false); }
  };
  useEffect(() => { if (open && items.length === 0) void search(); /* eslint-disable-line */ }, [open]);
  return (
    <div>
      <button onClick={() => setOpen((o) => !o)} style={{ ...cssToStyle(field), marginTop: 0, cursor: 'pointer', textAlign: 'left', color: '#5b4b9e', fontWeight: 600 }}>
        {open ? '▾' : '▸'} {label}
      </button>
      {open && (
        <div style={{ marginTop: 8, display: 'grid', gap: 8 }}>
          <div style={{ display: 'flex', gap: 6 }}>
            <input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && search()} placeholder="Search — city, abstract, team…" style={{ ...cssToStyle(field), marginTop: 0, flex: 1 }} />
            <button onClick={search} style={{ padding: '0 12px', borderRadius: 6, border: '1px solid #7468ae', background: '#f7f4ff', color: '#5b4b9e', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>Search</button>
          </div>
          {!enabled ? (
            <p style={{ fontSize: 11, color: '#9aa3af', margin: 0 }}>Stock search isn’t configured yet (needs a Pexels API key on the backend). Paste a URL above for now.</p>
          ) : loading ? (
            <p style={{ fontSize: 11, color: '#9aa3af', margin: 0 }}>Searching…</p>
          ) : items.length === 0 ? (
            <p style={{ fontSize: 11, color: '#9aa3af', margin: 0 }}>No results — try another search.</p>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6, maxHeight: 220, overflowY: 'auto' }}>
              {items.map((it) => (
                <button key={it.id} onClick={() => onPick(it)} title="Use this" style={{ padding: 0, border: '1px solid #e6e8ec', borderRadius: 6, overflow: 'hidden', cursor: 'pointer', background: '#000' }}>
                  <img src={it.thumbnail} alt="" style={{ width: '100%', height: 64, objectFit: 'cover', display: 'block' }} />
                </button>
              ))}
            </div>
          )}
          <p style={{ fontSize: 10, color: '#c7ccd4', margin: 0 }}>Stock via Pexels.</p>
        </div>
      )}
    </div>
  );
};
const VideoBgSettings = () => {
  const { props, actions: { setProp } } = useNode((node) => ({ props: node.data.props }));
  return (
    <div style={{ display: 'grid', gap: 10 }}>
      <p style={{ fontSize: 11, color: '#9aa3af', margin: 0 }}>Looping muted video behind an overlay. Drop content (text, a Free Layer) inside — it sits on top by z-order.</p>
      <label style={{ fontSize: 12, color: '#6b7280' }}>Video URL (mp4/webm — e.g. stock footage)
        <input style={cssToStyle(field)} type="text" value={props.videoUrl || ''} onChange={(e) => setProp((p: any) => (p.videoUrl = e.target.value))} placeholder="https://…/hero.mp4" />
      </label>
      <StockPicker kind="video" label="Browse stock video" onPick={(it) => setProp((p: any) => { p.videoUrl = it.url; if (!p.poster && it.thumbnail) p.poster = it.thumbnail; })} />
      <ImageUpload label="Poster (fallback image)" value={props.poster || ''} onChange={(v) => setProp((p: any) => (p.poster = v || ''))} />
      <div style={{ display: 'flex', gap: 8 }}>
        <label style={{ fontSize: 12, color: '#6b7280', flex: 1 }}>Overlay color
          <input style={cssToStyle(field)} type="text" value={props.overlayColor || ''} onChange={(e) => setProp((p: any) => (p.overlayColor = e.target.value))} placeholder="#000000" />
        </label>
        <label style={{ fontSize: 12, color: '#6b7280', flex: 1 }}>Overlay opacity
          <input style={cssToStyle(field)} type="number" step="0.05" min="0" max="1" value={props.overlayOpacity ?? 0.45} onChange={(e) => setProp((p: any) => (p.overlayOpacity = Number(e.target.value)))} />
        </label>
      </div>
      <label style={{ fontSize: 12, color: '#6b7280' }}>Min height (px)
        <input style={cssToStyle(field)} type="number" value={props.minHeight || 460} onChange={(e) => setProp((p: any) => (p.minHeight = Number(e.target.value)))} />
      </label>
    </div>
  );
};
VideoBgNode.craft = {
  displayName: 'Video background',
  props: { videoUrl: '', poster: '', overlayColor: '#000000', overlayOpacity: 0.45, minHeight: 460 },
  rules: { canMoveIn: () => true },
  related: { settings: VideoBgSettings },
};

// The default root content for a new document.
export const DefaultRoot = () => (
  <Element is={Container} canvas background="#fafaf7" padding={0} minHeight={400}>
    <Element is={TrivenyHeroNode} />
  </Element>
);

// Tiny helper: parse a `a:b;c:d` string into a style object (keeps the node
// file terse without pulling in a CSS-in-JS dep).
function cssToStyle(s: string): React.CSSProperties {
  const out: any = {};
  s.split(';').forEach((decl) => {
    const [k, v] = decl.split(':');
    if (!k || !v) return;
    out[k.trim().replace(/-([a-z])/g, (_, c) => c.toUpperCase())] = v.trim();
  });
  return out;
}
